// src/components/ClinicUserManagement/Clinicusermanagement.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios.secure';
import Menu from '../Menu/Menu';
import './css/clinicusermanagement.css';
import { useAuth } from '../../auth/AuthContext';

// ───────────────── 허용 문자 / 길이 규칙 (앱·웹 공통) ─────────────────
const MAX_ID_LEN = 28;
const MAX_PW_LEN = 32;

// ID: 영문/숫자 + . - _
const ID_CHAR_REGEX = /^[A-Za-z0-9._-]+$/;   // 전체 문자열 검사용
const ID_CHAR_ONE   = /^[A-Za-z0-9._-]$/;    // 한 글자 검사용

// 검색창: 한 글자 검사 (한글, 영문/숫자, ._-@, 공백)
const SEARCH_CHAR_ONE = /^[A-Za-z0-9._\-@가-힣\s]$/;

// 비밀번호: 영문/숫자 + !@#$%^*()_+-=
const PW_CHAR_ONE    = /^[A-Za-z0-9!@#$%^*()_+\-=]$/;      // 한 글자 검사용
const PW_ALLOWED_ALL = /^[A-Za-z0-9!@#$%^*()_+\-=]+$/;     // 전체 문자열 검사용

// 값 필터링 (붙여넣기 / drag 입력 대비)
const filterIdValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => ID_CHAR_ONE.test(ch))
    .join('')
    .slice(0, MAX_ID_LEN);

const filterPwValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => PW_CHAR_ONE.test(ch))
    .join('')
    .slice(0, MAX_PW_LEN);

const filterSearchValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => SEARCH_CHAR_ONE.test(ch))
    .join('')
    .slice(0, 50);

/** 상태 배지 */
function Badge({ s }) {
  const v = String(s || '').toUpperCase();
  const cls = v === 'ACTIVE' ? 'active' : 'locked';
  return <span className={`status-pill ${cls}`}>{v || '-'}</span>;
}

/** 비밀번호 강도 검사(클리닉/어드민 페이지와 동일 규칙) */
const pwStrong = (pw, id) => {
  if (typeof pw !== 'string') return false;
  if (pw.length < 10 || pw.length > 32) return false;      // 서버 정책 기준
  if (/\s/.test(pw)) return false;

  // 허용 특수문자만 사용 (앱/다른 페이지와 동일)
  if (!PW_ALLOWED_ALL.test(pw)) return false;

  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSpec = /[^A-Za-z0-9]/.test(pw);
  const kinds = [hasLower, hasUpper, hasDigit, hasSpec].filter(Boolean).length;
  if (kinds < 3) return false;
  if (id && pw.toLowerCase().includes(String(id).toLowerCase())) return false;
  return true;
};

/** 서버 응답 성공 판별(래핑 형태 포함) */
const isOk = (res) => {
  if (!res) return false;
  if (res?.status === true || res?.ok === true) return true;
  if (res?.messageCode === 200 && (res?.data?.status === true || res?.data?.ok === true)) return true;
  if (res?.data?.data?.status === true || res?.data?.data?.ok === true) return true;
  return false;
};

/** 서버 메시지 추출(없으면 fallback) */
const getMsg = (res, fallback) =>
  res?.messageString || res?.message || res?.data?.message || res?.data?.messageString || fallback;

export default function ClinicUserManagement() {
  // 공통 UI 상태
  const { user } = useAuth(); // ADMIN / CLINIC 판별
  const role = String(user?.role || '').toUpperCase();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2200); };

  // 목록/검색/페이지네이션
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (size || 10))),
    [total, size]
  );

  // 행별 비밀번호 입력 임시 상태
  const [pwDraft, setPwDraft] = useState({}); // { [user_id]: 'newpw' }

  // 생성 폼
  const [createForm, setCreateForm] = useState({ clinic_id: '', user_id: '', user_password: '', status: 'ACTIVE' });

  // ───────── 입력 차단용 핸들러들 ─────────
  const handleBeforeInputId = (e) => {
    if (e.isComposing) return;
    const ch = e.data;
    if (!ch) return; // Backspace 등
    if (!ID_CHAR_ONE.test(ch)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= MAX_ID_LEN) {
      e.preventDefault();
    }
  };

  const handleBeforeInputPw = (e) => {
    if (e.isComposing) return;
    const ch = e.data;
    if (!ch) return;
    if (!PW_CHAR_ONE.test(ch)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= MAX_PW_LEN) {
      e.preventDefault();
    }
  };

  const handleBeforeInputSearch = (e) => {
    const ch = e.data;
    if (!ch) return;
    if (!SEARCH_CHAR_ONE.test(ch)) {
      e.preventDefault();
    }
  };

  /** 공통 payload 파서(래핑/중첩 모두 수용) */
  const parsePayload = (raw) => {
    if (!raw) return {};
    const payload = raw?.data?.data || raw?.data || raw;

    const rows = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.list)
        ? payload.list
        : Array.isArray(payload) ? payload : [];

    const pageV = Number(payload?.page ?? 1);
    const sizeV = Number(payload?.size ?? 10);
    const totV = Number(payload?.total ?? payload?.count ?? 0);

    return { rows, pageV, sizeV, totV };
  };

  // ───────── 목록 ─────────
  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, size };
      const t = (q || '').trim();
      if (t.length >= 2) params.q = t;               // 2자 이상 검색 적용

      const { data } = await api.get('/manager/user/clinic/users', { params });
      const { rows, pageV, sizeV, totV } = parsePayload(data);

      setList(Array.isArray(rows) ? rows : []);
      setPage(pageV || 1);
      setSize(sizeV || 10);
      setTotal(totV || 0);

      if (t && t.length < 2) toast('검색은 2자 이상 입력 시 적용됩니다.');
    } catch (e) {
      console.error('fetchList error:', e);
      setList([]); setTotal(0);
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else toast('목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchList(1); /* eslint-disable-next-line */ }, []);

  // ───────── 상태변경 ─────────
  const changeStatus = async (user_id, next) => {
    setLoading(true);
    try {
      const { data } = await api.patch(
        `/manager/user/clinic/users/${encodeURIComponent(user_id)}/status`,
        { status: next }
      );
      console.log('changeStatus response data:', data);
      if (isOk(data)) {
        setList(prev => prev.map(r => r.user_id === user_id ? ({ ...r, status: next }) : r));
        toast('상태 변경 완료');
      } else {
        toast(getMsg(data, '상태 변경 실패'));
      }
    } catch (e) {
      console.error('changeStatus error:', e);
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else toast('상태 변경 실패');
    } finally {
      setLoading(false);
    }
  };

  // ───────── 비밀번호 변경(행 단위) ─────────
  const changePassword = async (user_id) => {
    const pw = pwDraft[user_id] || '';
    if (!pwStrong(pw, user_id)) return toast('비밀번호(10~32자, 공백X, 3종 이상, ID 포함 금지, 허용 특수문자만 사용 가능)');

    setLoading(true);
    try {
      const { data } = await api.post(
        `/manager/user/clinic/users/${encodeURIComponent(user_id)}/password`,
        { new_password: pw }
      );
      if (isOk(data)) {
        toast('비밀번호 변경 완료');
        setPwDraft(d => ({ ...d, [user_id]: '' })); // 입력창 초기화
        fetchList(page); // pw_changed_at 반영
      } else {
        toast(getMsg(data, '비밀번호 변경 실패'));
      }
    } catch (e) {
      console.error('changePassword error:', e);
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else if (e?.response?.status === 406 || e?.response?.status === 409) toast('비밀번호 정책 위반');
      else toast('비밀번호 변경 실패');
    } finally {
      setLoading(false);
    }
  };

  // ───────── 생성 ─────────
  const createUser = async () => {
    const { clinic_id, user_id, user_password, status } = createForm;
    if (!user_id || !user_password) return toast('아이디/비밀번호를 입력하세요');
    if (role === 'ADMIN' && !clinic_id) return toast('ADMIN은 clinic_id를 입력해야 합니다');

    // ID 형식 검사 (user_id / clinic_id 둘 다)
    if (!ID_CHAR_REGEX.test(user_id)) {
      return toast('user_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }
    if (role === 'ADMIN' && clinic_id && !ID_CHAR_REGEX.test(clinic_id)) {
      return toast('clinic_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }

    if (!pwStrong(user_password, user_id)) {
      return toast('비밀번호(10~32자, 공백X, 3종 이상, ID 포함 금지, 허용 특수문자만 사용 가능)');
    }

    setLoading(true);
    try {
      const body = {
        user_id,
        user_password,
        status: status || 'ACTIVE',
      };
      if (role === 'ADMIN') body.clinic_id = clinic_id; // ✅ ADMIN만 clinic_id 포함
      const { data } = await api.post('/manager/user/clinic/users', body);
      if (isOk(data)) {
        toast('생성 완료');
        setCreateForm({ clinic_id: '', user_id: '', user_password: '', status: 'ACTIVE' });
        fetchList(1);
      } else {
        toast(getMsg(data, '생성 실패'));
      }
    } catch (e) {
      console.error('createUser error:', e);
      if (e?.response?.status === 409) toast('중복된 계정입니다.');
      else if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else if (e?.response?.status === 406) toast('비밀번호 정책 위반');
      else toast('생성 실패');
    } finally {
      setLoading(false);
    }
  };

  const confirmAnd = async (message, fn) => { if (window.confirm(message)) await fn(); };

  return (
    <Menu>
      <div className="account-page cmu">
        <h1 className="text-2xl font-bold">사용자 계정 관리</h1>
        {msg && <div className="cmu-toast">{msg}</div>}

        {/* 목록 */}
        <section className="border rounded p-4 space-y-3">
          <h2 className="font-semibold">계정 목록</h2>

          {/* 검색/컨트롤 */}
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="flex gap-2 flex-1">
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="user_id 검색 (2자 이상)"
                value={q}
                onChange={(e) => setQ(filterSearchValue(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchList(1); }}
                onBeforeInput={handleBeforeInputSearch}
              />
              <select
                className="border rounded px-3 py-2"
                value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
                aria-label="페이지당 개수"
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}개</option>)}
              </select>
              <button
                type="button"
                onClick={() => fetchList(1)}
                disabled={loading}
                className="px-4 py-2 rounded border border-slate-400 text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors"
              >
                검색
              </button>
            </div>
          </div>

          {/* 테이블 */}
          <div className="overflow-x-auto border rounded mt-2">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">user_id</th>
                  <th className="text-left p-2">status</th>
                  <th className="text-left p-2">pw_changed_at</th>
                  <th className="text-left p-2">last_login</th>
                  <th className="text-left p-2">actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.user_id} className="border-t hover:bg-gray-50">
                    <td className="p-2 font-medium">{row.user_id}</td>
                    <td className="p-2"><Badge s={row.status} /></td>
                    <td className="p-2">{row.pw_changed_at ? new Date(row.pw_changed_at).toLocaleString() : '-'}</td>
                    <td className="p-2">{row.last_login ? new Date(row.last_login).toLocaleString() : '-'}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        {row.status === 'ACTIVE' ? (
                          <button
                            type="button"
                            className="px-2 py-1 border rounded bg-amber-600 text-black"
                            onClick={() => confirmAnd('해당 계정을 잠금(LOCK) 처리할까요?', () => changeStatus(row.user_id, 'LOCKED'))}
                            disabled={loading}
                          >잠금</button>
                        ) : (
                          <button
                            type="button"
                            className="px-2 py-1 border rounded bg-green-600 text-black"
                            onClick={() => confirmAnd('해당 계정을 활성화(ACTIVE)로 변경할까요?', () => changeStatus(row.user_id, 'ACTIVE'))}
                            disabled={loading}
                          >활성</button>
                        )}

                        <input
                          type="password"
                          className="border rounded px-2 py-1"
                          placeholder="새 비밀번호"
                          value={pwDraft[row.user_id] || ''}
                          onChange={(e) =>
                            setPwDraft(d => ({ ...d, [row.user_id]: filterPwValue(e.target.value) }))
                          }
                          onBeforeInput={handleBeforeInputPw}
                        />
                        <button
                          type="button"
                          className="px-2 py-1 border rounded bg-purple-600 text-black"
                          onClick={() => confirmAnd('비밀번호를 변경하시겠습니까?', () => changePassword(row.user_id))}
                          disabled={loading || !(pwDraft[row.user_id] || '').length}
                        >변경</button>
                        <button
                          type="button"
                          className="px-2 py-1 border rounded bg-blue-600 text-black"
                          onClick={() => navigate(`/clinic/users/${encodeURIComponent(row.user_id)}/info`)}
                        >
                          개인정보
                        </button>

                      </div>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr><td className="p-4 text-center text-gray-500" colSpan={5}>데이터가 없습니다</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">총 {total}건 / {page} / {totalPages} 페이지</div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page <= 1 || loading}
                onClick={() => fetchList(page - 1)}
              >이전</button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page >= totalPages || loading}
                onClick={() => fetchList(page + 1)}
              >다음</button>
            </div>
          </div>
        </section>

        {/* 생성 */}
        <section className="border rounded p-4 space-y-3">
          <h2 className="font-semibold">사용자 계정 생성</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* ADMIN만 clinic_id 입력 */}
            {role === 'ADMIN' && (
              <input
                className="border rounded px-3 py-2"
                placeholder="clinic_id (ADMIN 전용)"
                value={createForm.clinic_id}
                onChange={(e) =>
                  setCreateForm(f => ({ ...f, clinic_id: filterIdValue(e.target.value) }))
                }
                onBeforeInput={handleBeforeInputId}
              />
            )}
            <input
              className="border rounded px-3 py-2"
              placeholder="user_id"
              value={createForm.user_id}
              onChange={(e) =>
                setCreateForm(f => ({ ...f, user_id: filterIdValue(e.target.value) }))
              }
              onBeforeInput={handleBeforeInputId}
            />
            <input
              type="password"
              className="border rounded px-3 py-2"
              placeholder="password (10~32자, 공백없음, 3종 이상)"
              value={createForm.user_password}
              onChange={(e) =>
                setCreateForm(f => ({ ...f, user_password: filterPwValue(e.target.value) }))
              }
              onBeforeInput={handleBeforeInputPw}
            />
            <div className="flex items-center gap-2">
              <select
                className="border rounded px-3 py-2"
                value={createForm.status}
                onChange={(e) => setCreateForm(f => ({ ...f, status: e.target.value }))}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="LOCKED">LOCKED</option>
              </select>
              <button
                type="button"
                className="px-4 py-2 rounded border text-black disabled:opacity-50"
                onClick={createUser}
                disabled={loading}
              >생성</button>
              <button
                type="button"
                className="px-4 py-2 rounded border"
                onClick={() => setCreateForm({ clinic_id: '', user_id: '', user_password: '', status: 'ACTIVE' })}
              >초기화</button>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            * 비밀번호는 10~32자, 공백 금지, 대/소문자/숫자/허용 특수문자 중 3종류 이상 포함, ID 포함 금지
          </p>
        </section>

        {msg && <div className="cmu-toast">{msg}</div>}
      </div>
    </Menu>
  );
}
